import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import duration from "dayjs/plugin/duration";
import localizedFormat from "dayjs/plugin/localizedFormat";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";
import advancedFormat from "dayjs/plugin/advancedFormat";
import React, { useState } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import useAppStore from "../stores/appStore";
import { selectedAccountIdSelector, selectedAssistantSelector } from "../stores/selectors/appSelectors";
import { useFetchAccountDetails } from "../queries/hooks/accounts";
import { DEFAULT_TIMEZONE, TIMEZONE_DISPLAY_NAMES } from "@consts";

// dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);

const DateTimeContext = React.createContext(null);

const DEFAULT_DATE_RANGE = dayjs.duration(1, "w");

const DateTimeProvider = ({ children }) => {
  const selectedAssistant = useAppStore(selectedAssistantSelector);
  const selectedAccountId = useAppStore(selectedAccountIdSelector);
  const { enableAccountOverview } = useFlags();
  // Account-level pages have no selected assistant — fall back to the account timezone.
  // Gated on the account-overview flag: it is the only consumer, and the details
  // endpoint should not be hit for users outside the rollout.
  const shouldFetchAccountDetails = !selectedAssistant && Boolean(enableAccountOverview);
  const { data: accountDetails, isLoading: isAccountDetailsLoading } = useFetchAccountDetails(shouldFetchAccountDetails ? selectedAccountId : null);
  const timezoneKey = selectedAssistant?.timezone || accountDetails?.timezone || DEFAULT_TIMEZONE;
  const timeZone = TIMEZONE_DISPLAY_NAMES[timezoneKey]?.timezone;
  // Display forms of the same resolved key. Consumers must not re-derive these from
  // selectedAssistant alone: that chain misses the account fallback and so labels
  // account-level pages with the wrong timezone. Unknown keys fall back to the raw
  // key rather than rendering an empty parenthetical.
  const timezoneName = TIMEZONE_DISPLAY_NAMES[timezoneKey]?.long ?? timezoneKey;
  const timezoneShortName = TIMEZONE_DISPLAY_NAMES[timezoneKey]?.short ?? timezoneKey;
  // False only while the account timezone is still in flight — consumers that compute
  // date boundaries at mount (e.g. VoiceOverview) should wait for it.
  const isTimeZoneResolved = !shouldFetchAccountDetails || !isAccountDetailsLoading;

  const getTimeZoneDate = (timeString, overrideTimezone) => dayjs.tz(dayjs(timeString), overrideTimezone || timeZone);

  const localDate = (timeString) => dayjs(timeString);

  const formattedTimezone = (timeString) => getTimeZoneDate(timeString).format("L LT");

  const formattedTimezoneSeconds = (timeString) =>
    getTimeZoneDate(timeString).format("hh:mm:ss A");

  const formattedTimezoneMilliSeconds = (timeString) =>
    getTimeZoneDate(timeString).format("hh:mm:ss.SSS A");

  const [fromDate, setFromDate] = useState(
    () => getTimeZoneDate().subtract(DEFAULT_DATE_RANGE).startOf("d").toDate()
  );
  const [toDate, setToDate] = useState(() => getTimeZoneDate().toDate());

  const ctxValue = {
    timeZone,
    timezoneKey,
    timezoneName,
    timezoneShortName,
    isTimeZoneResolved,
    getTimeZoneDate,
    localDate,
    formattedTimezone,
    formattedTimezoneSeconds,
    formattedTimezoneMilliSeconds,
    fromDate,
    toDate,
    setFromDate,
    setToDate
  };

  return (
    <DateTimeContext.Provider value={ctxValue}>
      {children}
    </DateTimeContext.Provider>
  );
};

export default DateTimeContext;

export { DateTimeProvider };
